import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { DxDataGridModule } from 'devextreme-angular';
import { SchedulerTaskService } from 'src/app/configuration/services/scheduler-task.service';
import { SchedulerTaskHandler } from 'src/app/configuration/models/scheduler-task-objects';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { CurrentUserService } from '../../user/services';
import { ArtifactAssemblyTypes, AssemblyArtifact } from '../models/artifact';
import { DeveloperArtifactService } from '../services/developer-artifact.service';
import { GridService } from 'src/app/shared/grid/grid.service';
import { DeveloperArtifactAssemblyPickerComponent } from '../developer-artifacts/developer-artifact-assembly-picker.component';
import { DeveloperService } from '../services/developer.service';
import { BizDataGridDirective } from 'src/app/shared/grid/biz-grid.directive';
import { PermissionRequiredDirective } from 'src/app/shared/authentication/permission-required.directive';
import { ChangeLogLinkEntityDirective } from 'src/app/shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { ChangeLogLinkComponent } from 'src/app/shared/change-log/change-log-link/change-log-link.component';

@Component({
  selector: 'app-developer-scheduler-task-handlers',
  standalone: true,
  imports: [SvgIconComponent, DxDataGridModule, ChangeLogLinkComponent, DeveloperArtifactAssemblyPickerComponent, BizDataGridDirective, PermissionRequiredDirective, ChangeLogLinkEntityDirective],
  templateUrl: './developer-scheduler-task-handlers.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './developer-scheduler-task-handlers.component.scss'
})
export class DeveloperSchedulerTaskHandlersComponent implements OnInit {

  hasCreatePerm = false;
  hasEditPerm = false;
  hasDeletePerm = false;
  hasShowLogPerm = false;

  schedulerTaskHandlers: SchedulerTaskHandler[] = [];
  artifacts: AssemblyArtifact[] = [];

  constructor(
    private schedulerTaskService: SchedulerTaskService,
    private loadingPanelService: LoadingPanelService,
    private currentUserService: CurrentUserService,
    private developerArtifactService: DeveloperArtifactService,
    private gridService: GridService,
    private developerService: DeveloperService) {
    this.allowUpdatingRow = this.allowUpdatingRow.bind(this);
    this.allowDeletingRow = this.allowDeletingRow.bind(this);
    this.displayExprArtifact = this.displayExprArtifact.bind(this);
  }

  async ngOnInit(){
    this.artifacts = await this.developerArtifactService.getAssemblyArtifacts(true, [ArtifactAssemblyTypes.TypeSchedulerTaskHandler]);
    this.hasCreatePerm = this.currentUserService.hasPermission('Developer_Action_SchedulerTaskHandler_Create')
    this.hasEditPerm = this.currentUserService.hasPermission('Developer_Action_SchedulerTaskHandler_Edit')
    this.hasDeletePerm = this.currentUserService.hasPermission('Developer_Action_SchedulerTaskHandler_Delete')
    this.hasShowLogPerm = this.currentUserService.hasPermission('Developer_Action_ShowLog')
    this.refresh();
  }

  async refresh(){
    this.loadingPanelService.showLoadingPanelDirect('Loading scheduler task handlers...');
    this.schedulerTaskHandlers = await this.schedulerTaskService.getSchedulerTaskHandlers();
    this.loadingPanelService.hideLoadingPanel();
  }

  onInitNewRow(e: any): void {
    e.data = { Id: null, Name: '', Description: '', IsSystem: false, IsUserAvailable: true } as SchedulerTaskHandler;
  }

  onRowInserting = (e: any): void => {
    e.cancel = this.gridService.handleDataGridRow(e.data as SchedulerTaskHandler, item => this.saveScheduledTaskHandler(item));
  }

  onRowUpdating = (e: any): void => {
    e.cancel = this.gridService.handleDataGridRow({ ...e.key, ...e.newData } as SchedulerTaskHandler, item => this.saveScheduledTaskHandler(item), e.newData);
  };

  onRowRemoving = (e: any): void => {
    e.cancel = this.gridService.handleDataGridRow(e.data as SchedulerTaskHandler, item => this.removeScheduledTaskHandler(item));
  };


  allowUpdatingRow(options: { component: any; row: any }): boolean {
    const item: SchedulerTaskHandler = options?.row?.data;
    if (item) {
        return this.hasEditPerm && !item.IsSystem;
    }
    return false;
  }

  allowDeletingRow(options: { component: any; row: any }): boolean {
    const item: SchedulerTaskHandler = options?.row?.data;
    if (item) {
        return this.hasDeletePerm && !item.IsSystem;
    }
    return false;
  }

  allowShowLog(): boolean{ return this.hasShowLogPerm;}

  displayExprArtifact(e: any) {
    if (this.artifacts) {
        const artifact = this.artifacts.find(a => a.ArtifactId === e.value);
        if (artifact) {
            return `${artifact.Name} [${artifact.ClassName}] [${artifact.Filename}]`;
        }
    }
    return 'Unknown...';
  }

  async setArtifactId(artifactId: string, cellInfo: any) {
    cellInfo.setValue(artifactId || '');
  }

  async saveScheduledTaskHandler(schedulerTaskHandler: SchedulerTaskHandler): Promise<SchedulerTaskHandler> {
    return await this.developerService.saveSchedulerTaskHandler(schedulerTaskHandler);
  }

  async removeScheduledTaskHandler(schedulerTaskHandler: SchedulerTaskHandler): Promise<SchedulerTaskHandler> {
    await this.developerService.deleteSchedulerTaskHandler(schedulerTaskHandler.Id);
    return schedulerTaskHandler;
  }

  customizeItem(item: any) {
    if (item.dataField === 'IsSystem') {
      item.visible = false;
    }
    if (item.dataField === 'IsUserAvailable') {
      item.visible = false;
    }
  }

}
